import { useState } from "react";
import { depositCents, paths, type Customer, type Estimate } from "@clevis/shared-types";
import { doc, getDoc } from "firebase/firestore";
import { Button, Card, Input, Label } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { SignaturePad } from "../../components/SignaturePad";
import { useCardPayments } from "../../hooks/useCardPayments";

const money = (c: number) => `$${(c / 100).toFixed(2)}`;

type Step = "review" | "sign" | "done" | "declined";

// Present mode: the rep hands the device to the homeowner. Full-screen,
// customer-facing (no internal costs), big type, three steps:
// review → sign → done (email a copy / pay now / hand back).
export function PresentEstimate({
  estimate,
  companyName,
  onExit,
}: {
  estimate: Estimate & { id: string };
  companyName: string;
  onExit: () => void;
}) {
  const { tenantId } = useAuth();
  const [step, setStep] = useState<Step>("review");
  const [signerName, setSignerName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [emailState, setEmailState] = useState<"idle" | "sent" | "failed">("idle");
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const { ready: cardsReady } = useCardPayments(tenantId);
  const deposit = depositCents(estimate.deposit, estimate.totalCents);
  const depositLeft = Math.max(0, deposit - (estimate.depositPaidCents ?? 0));

  // The deposit on the company's Stripe; Stripe brings them back to the estimate page.
  async function payDeposit() {
    if (!tenantId || !estimate.signToken) return;
    setBusy(true);
    setError("");
    try {
      const res = await call<{ url: string }>("estimateDepositCheckout", { tenantId, estimateId: estimate.id, token: estimate.signToken }, { mutation: false });
      window.location.href = res.url;
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  async function approve() {
    if (!tenantId || !signature) return;
    setBusy(true);
    setError("");
    try {
      await call("approveEstimateOnSite", {
        tenantId,
        estimateId: estimate.id,
        signerName: signerName.trim(),
        signaturePngBase64: signature,
      });
      // Prefill the email with the customer's address if we have one.
      if (estimate.customerId) {
        const snap = await getDoc(doc(db, paths.tenantCustomer(tenantId, estimate.customerId)));
        setEmail(((snap.data() as Customer | undefined)?.email ?? "").trim());
      }
      setStep("done");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    if (!tenantId) return;
    setBusy(true);
    try {
      await call("setEstimateStatus", { tenantId, estimateId: estimate.id, action: "send" }).catch(() => null);
      await call("setEstimateStatus", { tenantId, estimateId: estimate.id, action: "decline" });
      setStep("declined");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function sendEmail() {
    if (!tenantId || !email) return;
    setBusy(true);
    setError("");
    try {
      await call("sendSalesDocEmail", {
        tenantId,
        docId: estimate.id,
        kind: "estimate",
        toEmail: email.trim(),
        message: `Thanks ${signerName.split(" ")[0] ?? ""}! Here's your signed estimate from ${companyName}.`,
      });
      setEmailState("sent");
    } catch (err) {
      setEmailState("failed");
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function chargeNow() {
    if (!tenantId) return;
    setBusy(true);
    setError("");
    try {
      let invId = invoiceId;
      if (!invId) {
        const conv = await call<{ invoiceId: string }>("convertEstimateToInvoice", {
          tenantId,
          estimateId: estimate.id,
        });
        invId = conv.invoiceId;
        setInvoiceId(invId);
        await call("setInvoiceStatus", { tenantId, invoiceId: invId, action: "send" });
      }
      const res = await call<{ url: string }>(
        "createInvoicePayLink",
        { tenantId, invoiceId: invId },
        { mutation: false },
      );
      window.location.href = res.url; // homeowner pays right on this device
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-cream">
      <div className="mx-auto max-w-lg p-4 pb-16">
        {/* Discreet exit for the rep */}
        <button className="mb-2 text-xs text-steel" onClick={onExit}>
          ← back (staff)
        </button>

        {step === "review" ? (
          <>
            <Card className="mb-4">
              <p className="text-xs uppercase tracking-wide text-steel">Estimate #{estimate.number}</p>
              <h1 className="mb-1 text-xl font-bold text-ink">{companyName}</h1>
              {estimate.customerName ? <p className="mb-3 text-sm text-steel">Prepared for {estimate.customerName}</p> : null}
              <div className="divide-y divide-steel/10">
                {estimate.lines.map((l, i) => {
                  const hidden = l.priceVisibility === "hide";
                  return (
                    <div key={i} className="flex items-start justify-between gap-3 py-2">
                      {l.photoUrl ? <img src={l.photoUrl} alt="" className="h-16 w-16 shrink-0 rounded-lg border border-steel/15 object-cover" /> : null}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ink">{l.description}</p>
                        {l.quantity !== 1 && !hidden ? (
                          <p className="text-xs text-steel">
                            {l.quantity} × {money(l.unitPriceCents)}
                          </p>
                        ) : null}
                        {l.components && l.components.length > 0 && l.showComponents !== false ? (
                          <div className="mt-0.5 pl-2 text-xs text-steel">
                            {l.components.map((c, ci) => (
                              <div key={ci}>
                                • {c.quantity !== 1 ? `${c.quantity} × ` : ""}
                                {c.name}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      {!hidden ? (
                        <p className="shrink-0 text-sm font-medium text-ink">
                          {money(Math.round(l.quantity * l.unitPriceCents))}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 border-t border-steel/20 pt-2 text-right">
                <p className="text-xs text-steel">
                  Subtotal {money(estimate.subtotalCents)} · Tax {money(estimate.taxCents)}
                </p>
                <p className="text-2xl font-bold text-ink">{money(estimate.totalCents)}</p>
              </div>
              {deposit > 0 ? (
                <p className="mt-1 text-right text-sm text-ink">
                  Deposit when signed: <span className="font-semibold">{money(deposit)}</span>
                </p>
              ) : null}
              {estimate.notes ? <p className="mt-2 text-xs text-steel">{estimate.notes}</p> : null}
            </Card>
            {estimate.terms ? (
              <Card className="mb-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-steel">Terms and conditions</p>
                <div className="max-h-60 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-ink">{estimate.terms}</div>
              </Card>
            ) : null}
            <div className="space-y-2">
              <Button className="w-full py-3 text-base" onClick={() => setStep("sign")}>
                Looks good — approve & sign
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => void decline()} busy={busy}>
                Not today
              </Button>
            </div>
          </>
        ) : null}

        {step === "sign" ? (
          <Card>
            <h2 className="mb-1 text-lg font-bold text-ink">Approve estimate #{estimate.number}</h2>
            <p className="mb-3 text-sm text-steel">
              Total {money(estimate.totalCents)}
              {deposit > 0 ? `, with a ${money(deposit)} deposit` : ""}. By signing you approve this work at this price
              {estimate.terms ? " and agree to the terms and conditions" : ""}.
            </p>
            <div className="mb-3">
              <Label>Your name</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Full name"
                autoComplete="off"
              />
            </div>
            <SignaturePad onChange={setSignature} />
            {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setStep("review")}>
                Back
              </Button>
              <Button
                className="flex-1 py-3"
                onClick={() => void approve()}
                busy={busy}
                disabled={!signature || signerName.trim().length < 2}
              >
                Approve
              </Button>
            </div>
          </Card>
        ) : null}

        {step === "done" ? (
          <Card className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-2xl">
              ✓
            </div>
            <h2 className="mb-1 text-lg font-bold text-ink">Approved — thank you!</h2>
            <p className="mb-4 text-sm text-steel">
              {signerName} approved estimate #{estimate.number} for {money(estimate.totalCents)}.
            </p>
            <div className="mb-3 text-left">
              <Label>Email a signed copy to</Label>
              <div className="flex gap-2">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                <Button onClick={() => void sendEmail()} busy={busy} disabled={!email || emailState === "sent"}>
                  {emailState === "sent" ? "Sent ✓" : "Send"}
                </Button>
              </div>
            </div>
            {cardsReady && depositLeft > 0 ? (
              <Button className="mb-2 w-full py-3" onClick={() => void payDeposit()} busy={busy} disabled={!estimate.signToken}>
                Pay the {money(depositLeft)} deposit by card
              </Button>
            ) : cardsReady && deposit === 0 ? (
              <Button className="mb-2 w-full py-3" onClick={() => void chargeNow()} busy={busy}>
                Pay now by card
              </Button>
            ) : depositLeft > 0 ? (
              <p className="mb-2 text-sm text-ink">Deposit due: {money(depositLeft)}. Take it by check or cash and record it on the estimate.</p>
            ) : null}
            <Button variant="ghost" className="w-full" onClick={onExit}>
              Done — hand back to {companyName}
            </Button>
            {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
          </Card>
        ) : null}

        {step === "declined" ? (
          <Card className="text-center">
            <h2 className="mb-1 text-lg font-bold text-ink">No problem.</h2>
            <p className="mb-4 text-sm text-steel">The estimate stays on file if you change your mind.</p>
            <Button variant="ghost" className="w-full" onClick={onExit}>
              Hand back
            </Button>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
